import React, { useEffect, useState } from 'react';
import { StyleSheet } from 'react-native';
import Animated, {
    Easing,
    useAnimatedStyle,
    useSharedValue,
    withDelay,
    withRepeat,
    withTiming,
} from 'react-native-reanimated';

import OpponentAvatar from '@/assets/icons/opponentAvatar';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';

const AnimatedBox = Animated.createAnimatedComponent(Box);

const ANIMATION_DURATION = 3000;
export const AVATAR_SIZE = 104;

type PulseRingProps = {
    delay: number;
    delayRender: boolean;
};

const PulseRing = ({ delay, delayRender }: PulseRingProps) => {
    const scale = useSharedValue(1);
    const opacity = useSharedValue(0.7);
    const borderWidth = useSharedValue(8);
    const [isVisible, setIsVisible] = useState(delayRender ? false : true);

    useEffect(() => {
        setTimeout(() => {
            setIsVisible(true);
        }, delay);
    }, [delay]);

    // Start animation when component mounts
    useEffect(() => {
        // Scale from 1 to 1.5 (which will appear as growing from the size of the dot to much larger)
        scale.value = withDelay(
            delay,
            withRepeat(
                withTiming(1.5, {
                    duration: ANIMATION_DURATION,
                    easing: Easing.out(Easing.ease),
                }),
                -1, // Infinite repetitions
                false // Don't reverse the animation
            )
        );

        // Fade out as the ring expands, opacity goes from 0.7 to 0
        opacity.value = withDelay(
            delay,
            withRepeat(
                withTiming(0, {
                    duration: ANIMATION_DURATION,
                    easing: Easing.out(Easing.ease),
                }),
                -1, // Infinite repetitions
                false // Don't reverse the animation
            )
        );

        // Border width goes from 8 to 0
        borderWidth.value = withDelay(
            delay,
            withRepeat(withTiming(0, { duration: ANIMATION_DURATION, easing: Easing.out(Easing.ease) }), -1, false)
        );
    }, [borderWidth, delay, opacity, scale]);

    // Animated styles for the ring
    const ringStyle = useAnimatedStyle(() => {
        return {
            transform: [{ scale: scale.value }],
            opacity: opacity.value,
            borderWidth: borderWidth.value,
        };
    });

    if (!isVisible) {
        return null;
    }

    return (
        <AnimatedBox
            position="absolute"
            alignItems="center"
            justifyContent="center"
            height={117}
            width={117}
            borderRadius="r96"
            borderWidth={6}
            borderColor={ds.colorToken.icon.brand}
            style={ringStyle}
        />
    );
};

export const SearchingAvatar = () => {
    return (
        <Box
            width={AVATAR_SIZE}
            height={AVATAR_SIZE}
            backgroundColor={ds.colorToken.background.neutral.subtler}
            borderRadius="r96"
            borderWidth={1}
            style={styles.avatar}
            alignItems="center"
            justifyContent="center"
        >
            <PulseRing delay={200} delayRender={false} />
            <PulseRing delay={1200} delayRender={true} />
            <OpponentAvatar />
        </Box>
    );
};

const styles = StyleSheet.create({
    avatar: {
        borderColor: ds.colorValue.border.subtle,
    },
});
